import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageSection, ReportTable, Pagination, StatusBadge } from "../components/reporting";
import type { Column } from "../components/reporting";
import { statusMeta } from "../data/tenants";
import type { Tenant } from "../data/tenants";
import { api } from "../api";
import { useSession } from "../session";

const columns: Column[] = [
  { label: "Brand", align: "left" },
  { label: "Tenant key", align: "left", width: "170px" },
  { label: "Leads (30d)", align: "right", width: "110px" },
  { label: "Destinations", align: "right", width: "110px" },
  { label: "Failures", align: "right", width: "90px" },
];

type OutcomeType = "call_booked" | "consultation" | "proposal_sent" | "won" | "lost" | "custom";

interface OutcomeReport {
  totalLeads: number;
  leadsWithOutcomes: number;
  totalOutcomes: number;
  wonValuePence: number;
  byType: Record<OutcomeType, number>;
  items: Array<{
    id: string;
    leadId: string;
    leadName: string;
    leadStatus: "MQL" | "SQL";
    type: OutcomeType;
    valuePence: number | null;
    createdAt: string;
    createdBy: string;
  }>;
}

const outcomeLabels: Record<OutcomeType, string> = {
  call_booked: "Call booked",
  consultation: "Consultation held",
  proposal_sent: "Proposal sent",
  won: "Won",
  lost: "Lost",
  custom: "Custom",
};

const outcomeTypes = Object.keys(outcomeLabels) as OutcomeType[];
const sterling = new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" });

function KeyValue({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-micro text-tertiary">{label}</span>
      <span className={`text-body text-primary ${mono ? "font-mono text-caption break-all" : ""}`}>
        {value}
      </span>
    </div>
  );
}

function TenantDrawer({ tenant, onClose }: { tenant: Tenant | null; onClose: () => void }) {
  const navigate = useNavigate();
  const session = useSession();
  const [rotated, setRotated] = useState<{
    webhookUrl: string;
    apiKey: string;
    apiKeyHeader: string;
  } | null>(null);
  const [webhookCredentials, setWebhookCredentials] = useState<{
    apiKey: string | null;
    apiKeyHeader: string;
    rotationRequired: boolean;
  } | null>(null);
  const [credentialLoading, setCredentialLoading] = useState(false);
  const [keyVisible, setKeyVisible] = useState(false);
  const [keyCopied, setKeyCopied] = useState(false);
  const [keyRotation, setKeyRotation] = useState<{ targetVersion: number; status: string } | null>(
    null,
  );
  const [copied, setCopied] = useState(false);
  const [openingClient, setOpeningClient] = useState(false);
  const [accountSetupSent, setAccountSetupSent] = useState(false);
  const [error, setError] = useState("");
  const [outcomeReport, setOutcomeReport] = useState<OutcomeReport | null>(null);
  const [outcomesLoading, setOutcomesLoading] = useState(false);
  const [outcomesError, setOutcomesError] = useState("");
  useEffect(() => {
    setRotated(null);
    setWebhookCredentials(null);
    setCredentialLoading(false);
    setKeyVisible(false);
    setKeyCopied(false);
    setKeyRotation(null);
    setCopied(false);
    setOpeningClient(false);
    setAccountSetupSent(false);
    setError("");
  }, [tenant?.id]);
  useEffect(() => {
    const tenantId = tenant?.id;
    if (!tenantId) {
      setOutcomeReport(null);
      setOutcomesError("");
      setOutcomesLoading(false);
      return;
    }
    let active = true;
    setOutcomeReport(null);
    setOutcomesError("");
    setOutcomesLoading(true);
    api<OutcomeReport>(`/api/admin/tenants/${encodeURIComponent(tenantId)}/outcomes`)
      .then((report) => {
        if (active) setOutcomeReport(report);
      })
      .catch((problem) => {
        if (active) {
          setOutcomesError(
            problem instanceof Error ? problem.message : "Outcomes report unavailable",
          );
        }
      })
      .finally(() => {
        if (active) setOutcomesLoading(false);
      });
    return () => {
      active = false;
    };
  }, [tenant?.id]);
  if (!tenant) return null;
  const s = statusMeta[tenant.status];
  const inboundUrl = rotated?.webhookUrl ?? tenant.inboundUrl;
  const apiKey = rotated?.apiKey ?? webhookCredentials?.apiKey ?? null;
  const apiKeyHeader =
    rotated?.apiKeyHeader ?? webhookCredentials?.apiKeyHeader ?? "X-FMC-Webhook-Key";
  const rotationRequired = webhookCredentials?.rotationRequired ?? false;
  const setupWasSent = accountSetupSent || Boolean(tenant.ownerSetupSentAt);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div
        style={{ width: "min(640px, 96vw)" }}
        className="relative h-full bg-surface-white border-l border-default flex flex-col shadow-[var(--shadow-md)]"
      >
        <div className="shrink-0 px-[26px] py-[20px] border-b border-default flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2.5">
              <h2 className="text-title-md font-bold text-primary">{tenant.name}</h2>
              <StatusBadge label={s.label} tone={s.tone} />
            </div>
            <p className="text-caption text-tertiary mt-1">Created {tenant.createdAt}</p>
          </div>
          <button onClick={onClose} className="text-tertiary hover:text-primary transition-colors">
            <i className="fa-solid fa-xmark text-[17px]" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto scroll-thin px-[26px] py-[22px] flex flex-col gap-6">
          <div>
            <h3 className="text-caption font-semibold uppercase tracking-[0.06em] text-tertiary mb-3">
              Data isolation
            </h3>
            <div className="grid grid-cols-2 gap-4 rounded-md border border-default bg-surface-light px-4 py-3.5">
              <KeyValue label="Tenant key" value={tenant.key} mono />
              <KeyValue label="Storage region" value={tenant.region} />
              <KeyValue label="Retention" value={`${tenant.retentionDays} days`} />
              <KeyValue
                label="Wrapped key"
                value={`Tenant-specific AES key · version ${keyRotation?.targetVersion ?? tenant.keyVersion}`}
              />
            </div>
            <p className="text-micro text-tertiary mt-2">
              Lead records share the pooled database but are constrained by forced RLS and composite
              tenant keys, with contact fields encrypted under this tenant's own wrapped data key.
            </p>
            <button
              onClick={async () => {
                const password = window.prompt(
                  "Confirm your FMC admin password to rotate this tenant's data key",
                );
                if (!password) return;
                try {
                  const reauth = await api<{ token: string }>("/api/reauthenticate", {
                    method: "POST",
                    body: JSON.stringify({ password, action: "secret.rotate" }),
                  });
                  setKeyRotation(
                    await api<{ targetVersion: number; status: string }>(
                      `/api/admin/tenants/${encodeURIComponent(tenant.id)}/data-key/rotate`,
                      {
                        method: "POST",
                        body: JSON.stringify({ reauthenticationToken: reauth.token }),
                      },
                    ),
                  );
                } catch (problem) {
                  setError(problem instanceof Error ? problem.message : "Data-key rotation failed");
                }
              }}
              className="mt-3 h-8 px-3 rounded-xs border border-default text-caption font-semibold text-button"
            >
              Rotate tenant data key
            </button>
            {keyRotation && (
              <p className="text-caption text-status-green mt-2">
                Version {keyRotation.targetVersion} is active; background re-encryption is queued.
              </p>
            )}
          </div>

          <div>
            <h3 className="text-caption font-semibold uppercase tracking-[0.06em] text-tertiary mb-3">
              Inbound webhook
            </h3>
            <div className="flex items-center gap-2 rounded-md border border-default bg-surface-light px-3 py-2.5">
              <span className="min-w-0 flex-1 break-all font-mono text-caption text-primary">
                {inboundUrl}
              </span>
              <button
                type="button"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(inboundUrl);
                    setCopied(true);
                  } catch {
                    setError("The webhook URL could not be copied. Select and copy it manually.");
                  }
                }}
                className="shrink-0 h-8 px-2.5 rounded-xs border border-default bg-surface-white text-caption font-semibold text-button"
              >
                {copied ? "Copied" : "Copy URL"}
              </button>
            </div>
            <p className="text-micro text-tertiary mt-2">
              This route is always visible to FMC Admins and is unique per brand. It is not a
              secret: HubSpot must also send the separate API key shown below.
            </p>
            <div className="mt-3">
              <div className="text-micro font-semibold uppercase tracking-[0.06em] text-tertiary mb-1.5">
                API key
              </div>
              <div className="flex items-center gap-2 rounded-md border border-default bg-surface-light px-3 py-2.5">
                <span
                  aria-label="Webhook API key"
                  className="min-w-0 flex-1 break-all font-mono text-caption text-primary"
                >
                  {rotationRequired
                    ? "Not available for this existing route"
                    : `${apiKeyHeader}: ${apiKey && keyVisible ? apiKey : "••••••••••••••••••••••••"}`}
                </span>
                {!rotationRequired && (
                  <button
                    type="button"
                    disabled={credentialLoading}
                    onClick={async () => {
                      if (apiKey) {
                        setKeyVisible((visible) => !visible);
                        return;
                      }
                      setCredentialLoading(true);
                      setError("");
                      try {
                        const credentials = await api<{
                          apiKey: string | null;
                          apiKeyHeader: string;
                          rotationRequired: boolean;
                        }>(
                          `/api/admin/tenants/${encodeURIComponent(tenant.id)}/webhook-credentials`,
                        );
                        setWebhookCredentials(credentials);
                        setKeyVisible(Boolean(credentials.apiKey));
                      } catch (problem) {
                        setError(
                          problem instanceof Error ? problem.message : "API key unavailable",
                        );
                      } finally {
                        setCredentialLoading(false);
                      }
                    }}
                    className="shrink-0 h-8 px-2.5 rounded-xs border border-default bg-surface-white text-caption font-semibold text-button disabled:opacity-60"
                  >
                    {credentialLoading
                      ? "Loading"
                      : apiKey
                        ? keyVisible
                          ? "Hide key"
                          : "Show key"
                        : "Reveal key"}
                  </button>
                )}
                {apiKey && (
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(apiKey);
                        setKeyCopied(true);
                      } catch {
                        setError("The API key could not be copied. Reveal and copy it manually.");
                      }
                    }}
                    className="shrink-0 h-8 px-2.5 rounded-xs border border-default bg-surface-white text-caption font-semibold text-button"
                  >
                    {keyCopied ? "Copied" : "Copy key"}
                  </button>
                )}
              </div>
              {rotationRequired ? (
                <p className="text-micro text-status-amber mt-2">
                  This key was created before encrypted retrieval was enabled. Rotate the webhook
                  credentials once to create a key that can be viewed here.
                </p>
              ) : (
                <p className="text-micro text-tertiary mt-2">
                  Key access is restricted to FMC Admins, encrypted at rest and recorded in the
                  audit log.
                </p>
              )}
            </div>
            <button
              onClick={async () => {
                const password = window.prompt(
                  "Confirm your FMC admin password to rotate these credentials",
                );
                if (!password) return;
                try {
                  const reauth = await api<{ token: string }>("/api/reauthenticate", {
                    method: "POST",
                    body: JSON.stringify({ password, action: "secret.rotate" }),
                  });
                  const nextCredentials = await api<{
                    webhookUrl: string;
                    apiKey: string;
                    apiKeyHeader: string;
                  }>(
                    `/api/admin/tenants/${encodeURIComponent(tenant.id)}/webhook-credentials/rotate`,
                    {
                      method: "POST",
                      body: JSON.stringify({
                        overlapMinutes: 15,
                        reauthenticationToken: reauth.token,
                      }),
                    },
                  );
                  setRotated(nextCredentials);
                  setWebhookCredentials({
                    apiKey: nextCredentials.apiKey,
                    apiKeyHeader: nextCredentials.apiKeyHeader,
                    rotationRequired: false,
                  });
                  setKeyVisible(true);
                  setKeyCopied(false);
                } catch (problem) {
                  setError(problem instanceof Error ? problem.message : "Rotation failed");
                }
              }}
              className="mt-3 h-8 px-3 rounded-xs border border-default text-caption font-semibold text-button"
            >
              Rotate with 15-minute overlap
            </button>
            {rotated && (
              <div className="mt-3 rounded-md border border-status-amber/40 bg-surface-light px-3 py-3">
                <div className="text-caption font-semibold text-primary">
                  Credentials rotated. Update HubSpot before the 15-minute overlap expires.
                </div>
              </div>
            )}
            {error && <p className="text-caption text-status-red mt-2">{error}</p>}
          </div>

          <div>
            <h3 className="text-caption font-semibold uppercase tracking-[0.06em] text-tertiary mb-3">
              Activity
            </h3>
            <div className="grid grid-cols-3 gap-3">
              {[
                { v: tenant.leads30d.toLocaleString(), l: "Leads (30d)" },
                { v: String(tenant.destinations), l: "Outbound destinations" },
                { v: String(tenant.failures), l: "Failed deliveries" },
              ].map((m) => (
                <div key={m.l} className="rounded-md border border-default px-4 py-3">
                  <div className="text-stat font-bold text-primary leading-none">{m.v}</div>
                  <div className="text-micro text-tertiary mt-1.5">{m.l}</div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-baseline justify-between gap-3 mb-3">
              <h3 className="text-caption font-semibold uppercase tracking-[0.06em] text-tertiary">
                Lead outcomes report
              </h3>
              <span className="text-micro text-tertiary">All retained leads</span>
            </div>
            {outcomesLoading ? (
              <div className="rounded-md border border-default bg-surface-light px-4 py-5 text-caption text-tertiary">
                Loading outcomes…
              </div>
            ) : outcomesError ? (
              <div className="rounded-md border border-status-red/40 bg-surface-light px-4 py-4 text-caption text-status-red">
                {outcomesError}
              </div>
            ) : outcomeReport ? (
              <div className="flex flex-col gap-3">
                <div className="grid grid-cols-3 gap-3">
                  {[
                    {
                      value: `${outcomeReport.leadsWithOutcomes} / ${outcomeReport.totalLeads}`,
                      label: "Leads with outcomes",
                    },
                    { value: String(outcomeReport.totalOutcomes), label: "Outcome events" },
                    {
                      value: sterling.format(outcomeReport.wonValuePence / 100),
                      label: "Won value",
                    },
                  ].map((metric) => (
                    <div key={metric.label} className="rounded-md border border-default px-3 py-3">
                      <div className="text-heading font-bold text-primary leading-none">
                        {metric.value}
                      </div>
                      <div className="text-micro text-tertiary mt-1.5">{metric.label}</div>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-3 gap-px overflow-hidden rounded-md border border-default bg-default">
                  {outcomeTypes.map((type) => (
                    <div key={type} className="bg-surface-white px-3 py-2.5">
                      <div className="text-body font-semibold text-primary">
                        {outcomeReport.byType[type]}
                      </div>
                      <div className="text-micro text-tertiary mt-0.5">{outcomeLabels[type]}</div>
                    </div>
                  ))}
                </div>

                <div className="rounded-md border border-default overflow-hidden">
                  <div className="px-3.5 py-2.5 bg-surface-light border-b border-default flex items-center justify-between gap-3">
                    <span className="text-caption font-semibold text-primary">
                      Recent outcome activity
                    </span>
                    <span className="text-micro text-tertiary">
                      Showing {Math.min(outcomeReport.items.length, 10)}
                    </span>
                  </div>
                  {outcomeReport.items.length ? (
                    outcomeReport.items.slice(0, 10).map((outcome, index) => (
                      <div
                        key={outcome.id}
                        className={`px-3.5 py-3 flex items-start justify-between gap-4 ${index > 0 ? "border-t border-default" : ""}`}
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-body font-medium text-primary truncate">
                              {outcome.leadName}
                            </span>
                            <StatusBadge
                              label={outcome.leadStatus}
                              tone={outcome.leadStatus === "SQL" ? "green" : "blue"}
                            />
                          </div>
                          <div className="text-micro text-tertiary mt-1">
                            {new Date(outcome.createdAt).toLocaleString("en-GB")} ·{" "}
                            {outcome.createdBy}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-caption font-semibold text-primary">
                            {outcomeLabels[outcome.type]}
                          </div>
                          {outcome.valuePence !== null && (
                            <div className="text-micro text-tertiary mt-1">
                              {sterling.format(outcome.valuePence / 100)}
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="px-3.5 py-5 text-caption text-tertiary">
                      No lead outcomes have been logged for this brand yet.
                    </p>
                  )}
                </div>
              </div>
            ) : null}
          </div>

          <div>
            <h3 className="text-caption font-semibold uppercase tracking-[0.06em] text-tertiary mb-3">
              Brand owner
            </h3>
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-full bg-surface-info text-brand-dark-teal flex items-center justify-center text-caption font-semibold">
                {tenant.owner
                  .split(" ")
                  .map((n) => n[0])
                  .join("")}
              </span>
              <div>
                <div className="flex items-center gap-2">
                  <div className="text-body font-medium text-primary">{tenant.owner}</div>
                  <StatusBadge
                    label={
                      tenant.ownerEmailVerified
                        ? "Active"
                        : setupWasSent
                          ? "Setup sent"
                          : "Setup pending"
                    }
                    tone={tenant.ownerEmailVerified || setupWasSent ? "green" : "amber"}
                  />
                </div>
                <div className="text-caption text-tertiary mt-0.5">{tenant.ownerEmail}</div>
              </div>
            </div>
            {!tenant.ownerEmailVerified && (
              <button
                type="button"
                onClick={async () => {
                  try {
                    setError("");
                    await api(
                      `/api/admin/tenants/${encodeURIComponent(tenant.id)}/account-setup-email`,
                      {
                        method: "POST",
                        body: JSON.stringify({}),
                      },
                    );
                    setAccountSetupSent(true);
                  } catch (problem) {
                    setError(
                      problem instanceof Error
                        ? problem.message
                        : "Account setup email could not be sent",
                    );
                  }
                }}
                className="mt-3 h-9 px-3 rounded-xs border border-default text-caption font-semibold text-button"
              >
                {setupWasSent ? "Resend account setup" : "Send account setup"}
              </button>
            )}
          </div>

          <div className="rounded-md border border-default bg-surface-info/40 px-4 py-3 text-caption text-secondary">
            FMC administrators can manage this client's leads, delivery configuration, team and
            settings directly. Every action remains tenant-scoped and audit-logged.
          </div>
        </div>

        <div className="shrink-0 border-t border-default px-[26px] py-4 flex items-center gap-2.5">
          <button
            disabled={openingClient}
            onClick={async () => {
              setOpeningClient(true);
              setError("");
              const entered = await session.enterAdminTenant(tenant.id);
              if (!entered) {
                setError("Client administration could not be opened. Please try again.");
                setOpeningClient(false);
                return;
              }
              navigate("/", { replace: true });
            }}
            className="h-10 px-4 rounded-xs bg-brand-red text-on-brand text-body font-semibold flex items-center gap-2 disabled:opacity-50"
          >
            <i className="fa-solid fa-arrow-right-arrow-left text-[13px]" />
            {openingClient ? "Opening client…" : "Manage client"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function AdminBrands() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loadError, setLoadError] = useState("");
  const [selected, setSelected] = useState<Tenant | null>(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    api<{
      items: Array<{
        id: string;
        slug: string;
        name: string;
        status: string;
        createdAt: string;
        retentionDays: number;
        cellId: string;
        keyVersion: number;
        webhookUrl: string;
        leads: number;
        destinations: number;
        failures: number;
        owner: string;
        ownerEmail: string;
        ownerEmailVerified: boolean;
        ownerSetupSentAt: string | null;
      }>;
    }>("/api/admin/tenants")
      .then((response) => {
        setTenants(
          response.items.map((tenant) => ({
            id: tenant.id,
            key: tenant.slug,
            name: tenant.name,
            status:
              tenant.status === "active"
                ? "live"
                : tenant.status === "suspended"
                  ? "suspended"
                  : "provisioning",
            region: `London · ${tenant.cellId}`,
            inboundUrl: tenant.webhookUrl,
            destinations: tenant.destinations,
            leads30d: tenant.leads,
            failures: tenant.failures,
            owner: tenant.owner,
            ownerEmail: tenant.ownerEmail,
            ownerEmailVerified: tenant.ownerEmailVerified,
            ownerSetupSentAt: tenant.ownerSetupSentAt,
            createdAt: new Date(tenant.createdAt).toLocaleDateString("en-GB"),
            retentionDays: tenant.retentionDays,
            keyVersion: tenant.keyVersion,
          })),
        );
      })
      .catch((error: Error) => setLoadError(error.message));
  }, []);

  const filtered = tenants.filter(
    (t) =>
      !query.trim() ||
      t.name.toLowerCase().includes(query.toLowerCase()) ||
      t.key.includes(query.toLowerCase()),
  );

  const rows = filtered.map((t) => [
    <button onClick={() => setSelected(t)} className="flex items-center gap-3 text-left group">
      <span className="w-9 h-9 shrink-0 rounded-md bg-surface-light border border-default flex items-center justify-center text-micro font-bold text-secondary">
        {t.name.slice(0, 2).toUpperCase()}
      </span>
      <span className="min-w-0">
        <span className="block text-body font-medium text-primary group-hover:text-brand-red transition-colors truncate">
          {t.name}
        </span>
        <span className="block text-micro text-tertiary truncate">{t.owner}</span>
      </span>
    </button>,
    <span className="font-mono text-caption text-secondary">{t.key}</span>,
    <span className="text-body text-primary tabular-nums">{t.leads30d.toLocaleString()}</span>,
    <span className="text-body text-secondary tabular-nums">{t.destinations}</span>,
    <span
      className={`text-body tabular-nums ${t.failures > 0 ? "text-status-red" : "text-tertiary"}`}
    >
      {t.failures}
    </span>,
  ]);

  const totals = {
    live: tenants.filter((t) => t.status === "live").length,
    leads: tenants.reduce((a, t) => a + t.leads30d, 0),
    failures: tenants.reduce((a, t) => a + t.failures, 0),
  };

  return (
    <div className="flex flex-col gap-[24px]">
      <div className="grid grid-cols-4 gap-4">
        {[
          { v: String(tenants.length), l: "Brands", i: "fa-building" },
          { v: String(totals.live), l: "Live tenants", i: "fa-circle-check" },
          { v: totals.leads.toLocaleString(), l: "Leads routed (30d)", i: "fa-inbox" },
          { v: String(totals.failures), l: "Delivery failures", i: "fa-triangle-exclamation" },
        ].map((m) => (
          <div
            key={m.l}
            className="bg-surface-white border border-default rounded-xl px-[22px] py-[18px]"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-caption text-tertiary">{m.l}</span>
              <i className={`fa-solid ${m.i} text-[13px] text-tertiary`} />
            </div>
            <div className="text-stat font-bold text-primary leading-none">{m.v}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="relative">
          <i className="fa-solid fa-magnifying-glass text-[13px] text-tertiary absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search brand or tenant key"
            className="h-9 w-[300px] pl-9 pr-3 rounded-xs border border-default bg-surface-white text-body text-primary placeholder:text-tertiary focus:outline-none focus:border-brand-red/50 transition-colors"
          />
        </div>
        <button
          onClick={() => navigate("/admin/brands/new")}
          className="h-9 px-3.5 rounded-xs bg-brand-red text-on-brand text-body font-medium flex items-center gap-2 hover:bg-brand-red-hover transition-colors"
        >
          <i className="fa-solid fa-plus text-[12px]" />
          New brand
        </button>
      </div>

      <PageSection
        title={`${filtered.length} brands`}
        right={
          <span className="text-caption text-tertiary">
            Shared London cell · forced RLS, composite tenant keys and per-tenant encryption
          </span>
        }
        bodyClassName="px-[20px] pt-[16px] pb-[20px]"
      >
        <ReportTable columns={columns} rows={rows} />
        {loadError && <p className="py-4 text-caption text-status-red">{loadError}</p>}
        <Pagination from={1} to={filtered.length} total={filtered.length} />
      </PageSection>

      <TenantDrawer tenant={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
